import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import EbzModal from 'components/EbzModal';
import { Col, Select, Slider, Typography, Row, Divider, Form, InputNumber } from 'antd';
import currencyFormat from 'utils/currencyFormat';
import MarkupSlabList from './MarkupSlabList';
import BaseRateSlabList from './BaseRateSlabList';
import {
  CommissionCalculatorModesOptions,
  PERCENTAGE,
  RUPEE_SIGN,
  TypographyTitle,
  TypographyTitleSign,
  TypographyTextPara,
  selectCommissionMode,
  selectCommissionModeTransactionAmount,
  maxTransactionAmount
} from './const';

function CommissionCalculator({
  commissionModalOpen,
  handleCommissionDrawerClose,
  markupInitialValue,
  transactionAmountForm
}) {
  const [selectMode, setSelectMode] = useState(selectCommissionMode);
  const [transactionAmount, setTransactionAmount] = useState(selectCommissionModeTransactionAmount);
  const [markUpSlab, setmarkUpSlab] = useState({ markupSlab: [] });
  const [baseRateSlab, setBaseRateSlab] = useState({ baseRateSlab: [] });
  const [calculateMarkUpValue, setCalculateMarkUpValue] = useState(null);
  const [calculateBaseRateValue, setCalculateBaseRateValue] = useState(null);

  const formatter = (value) => `${RUPEE_SIGN} ${value}`;

  const updateBaseRateSlab = (dataElement, roundTransactionAmount) => {
    dataElement?.range.forEach((baseRateElement) => {
      const maxBaseAmount =
        baseRateElement?.max_amount === 'Infinity'
          ? maxTransactionAmount
          : baseRateElement?.max_amount;
      if (
        baseRateElement?.min_amount <= roundTransactionAmount &&
        maxBaseAmount >= roundTransactionAmount
      ) {
        setBaseRateSlab({ baseRateSlab: [baseRateElement] });
        if (baseRateElement?.base_rate_type === PERCENTAGE) {
          const baseRateCharges = (roundTransactionAmount * baseRateElement.base_rate_value) / 100;
          setCalculateBaseRateValue(baseRateCharges);
        } else {
          setCalculateBaseRateValue(baseRateElement?.base_rate_value);
        }
      }
    });
  };

  const updateMarkupSlab = (dataElement, roundTransactionAmount) => {
    dataElement?.range.forEach((markUpElement) => {
      const maxMarkupAmount =
        markUpElement?.max_amount === 'Infinity' ? maxTransactionAmount : markUpElement?.max_amount;
      if (
        markUpElement?.min_amount <= roundTransactionAmount &&
        maxMarkupAmount >= roundTransactionAmount
      ) {
        setmarkUpSlab({ markUpSlab: [markUpElement] });
        if (markUpElement?.commercial_type === PERCENTAGE) {
          const markUpCharges = (roundTransactionAmount * markUpElement.commercial_value) / 100;
          setCalculateMarkUpValue(markUpCharges);
        } else {
          setCalculateMarkUpValue(markUpElement?.commercial_value);
        }
      }
    });
  };

  const handleMarkUpInitiaValue = () => {
    markupInitialValue.forEach((element) => {
      const roundTransactionAmount = Math.round(transactionAmount);
      if (element.mode_type === selectMode) {
        if ('data' in element && element?.data !== undefined) {
          element?.data.forEach((dataElement) => {
            if (
              dataElement?.key === 'base_rate' &&
              'range' in dataElement &&
              dataElement?.range !== undefined
            ) {
              updateBaseRateSlab(dataElement, roundTransactionAmount);
            }
            if (
              dataElement?.key === 'default' &&
              'range' in dataElement &&
              dataElement.range !== undefined
            ) {
              updateMarkupSlab(dataElement, roundTransactionAmount);
            }
          });
        }
      }
    });
  };

  const handleChange = (value) => {
    setSelectMode(value);
    handleMarkUpInitiaValue();
  };

  const handleSliderTransactionAmount = (value) => {
    transactionAmountForm.setFieldValue('formTransactionAmount', value);
    setTransactionAmount(value);
    handleMarkUpInitiaValue();
  };

  const handleTransactionAmount = (value) => {
    setTransactionAmount(value);
    handleMarkUpInitiaValue();
  };

  const defaultTransaction = () => {
    setTransactionAmount(1000);
  };

  useEffect(() => {
    if (commissionModalOpen && selectMode) {
      handleMarkUpInitiaValue();
    }
  }, [selectMode, commissionModalOpen, transactionAmount]);

  useEffect(() => {
    if (commissionModalOpen) {
      defaultTransaction();
      handleMarkUpInitiaValue();
    }
  }, [commissionModalOpen]);

  return (
    <EbzModal
      title={
        <Typography.Text style={{ fontWeight: 600, fontSize: '20px' }}>
          Calculate Partner Commission
        </Typography.Text>
      }
      toggleModal={handleCommissionDrawerClose}
      isOpen={commissionModalOpen}
      width={1000}
    >
      <Row gutter={[8, 8]}>
        <Col lg={18}>
          <Typography.Title level={5}>Select Payment Mode</Typography.Title>
          <Select
            defaultValue={selectMode}
            style={{
              width: '30%',
              border: '2px solid var(--color-primary)',
              borderRadius: '7px'
            }}
            onChange={handleChange}
            options={CommissionCalculatorModesOptions}
          />
        </Col>
        <Col lg={6}>
          <Typography.Title level={5}>Merchant Transaction Value</Typography.Title>
          <Form form={transactionAmountForm}>
            <Form.Item
              name="formTransactionAmount"
              rules={[
                {
                  required: true,
                  message: 'Please enter valid amount'
                },
                {
                  validator: (_, value) => {
                    /* eslint no-else-return: "error" */
                    if (value !== undefined && value !== null && value !== '' && value === 0) {
                      return Promise.reject(new Error(`Please enter valid amount`));
                    } else if (
                      value !== undefined &&
                      value !== null &&
                      value !== '' &&
                      value > maxTransactionAmount
                    ) {
                      return Promise.reject(new Error(`max limit ${maxTransactionAmount}`));
                    } else if (value !== undefined && value !== null && value !== '' && value < 0) {
                      return Promise.reject(new Error(`Please enter positive amount`));
                    } else if (
                      value !== undefined &&
                      value !== null &&
                      value !== '' &&
                      !/^[0-9]+(?:\.[0-9]{1,2})?$/.test(value)
                    ) {
                      return Promise.reject(new Error(`Up to 2 decimal alowed`));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <InputNumber
                formatter={(v) => {
                  if (v !== '') return currencyFormat(v, false, false);
                  return v;
                }}
                onChange={(value) => handleTransactionAmount(value)}
                style={{ width: '100%' }}
                prefix={RUPEE_SIGN}
              />
            </Form.Item>
          </Form>
        </Col>
      </Row>
      <Row>
        <Col lg={24}>
          <Slider
            tooltip={{
              formatter
            }}
            value={transactionAmount}
            onChange={handleSliderTransactionAmount}
            max={maxTransactionAmount}
            styles={{
              track: {
                background: '#611af7'
              },
              tracks: {
                background: `linear-gradient(to right,rgb(119, 57, 255) 0%,rgb(134, 80, 252) 100%)`
              }
            }}
            min={1}
          />
        </Col>
      </Row>

      <Row>
        <Col lg={24}>
          <Typography.Text style={{ color: '#8F93A8', fontWeight: 400, fontSize: '14px' }}>
            Applicable Platform Charges
          </Typography.Text>
          <MarkupSlabList markUpSlab={markUpSlab} />
        </Col>
        <Col lg={24}>
          <Typography.Text style={{ color: '#8F93A8', fontWeight: 400, fontSize: '14px' }}>
            Applicable Base Rate
          </Typography.Text>
          <BaseRateSlabList baseRateSlab={baseRateSlab} />
        </Col>
      </Row>
      <Divider />

      <Row gutter={[8, 8]}>
        <Col lg={6}>
          <TypographyTitle>Platform Charges</TypographyTitle>
          <TypographyTextPara>
            {RUPEE_SIGN}
            {new Intl.NumberFormat('en-IN').format(calculateMarkUpValue)}
          </TypographyTextPara>
        </Col>
        <Col lg={2}>
          <TypographyTitleSign>-</TypographyTitleSign>
        </Col>
        <Col lg={6}>
          <TypographyTitle>Base Rate</TypographyTitle>
          <TypographyTextPara>
            {RUPEE_SIGN}
            {new Intl.NumberFormat('en-IN').format(calculateBaseRateValue)}
          </TypographyTextPara>
        </Col>
        <Col lg={2}>
          <TypographyTitleSign>=</TypographyTitleSign>
        </Col>
        <Col lg={6}>
          <TypographyTitle>Commission Amount</TypographyTitle>
          <Typography.Text style={{ color: 'green' }}>
            {RUPEE_SIGN}
            {new Intl.NumberFormat('en-IN').format(calculateMarkUpValue - calculateBaseRateValue)}
          </Typography.Text>
        </Col>
      </Row>
    </EbzModal>
  );
}

CommissionCalculator.propTypes = {
  commissionModalOpen: PropTypes.bool.isRequired,
  handleCommissionDrawerClose: PropTypes.func.isRequired,
  markupInitialValue: PropTypes.instanceOf(Object).isRequired,
  transactionAmountForm: PropTypes.func.isRequired
};

export default CommissionCalculator;
